/**
 * papounan/v1 client. The browser only sends choices and renders responses (invariant 1).
 * Every call resolves to {status, body}; a non-JSON answer gives body {} with the HTTP status; a network
 * failure gives status 0 and the GENERIC error code.
 *
 * /config and /rooms are open (content, no prices). Everything else needs the browser session (02 §11.2):
 * POST only, wrapped by session.guard() — renewed silently on 401 SESSION_REQUIRED.
 *
 * Page load = 2 PHP requests (02 §4.0, 0.5.8): POST /start (config + rooms + challenge), then POST /session
 * with the solved challenge, which sets the cookie and returns the calendar.
 */
import { createSession, solve } from './session.js';
import { context, forbid, mark, track, trackApi, trackLoadFailure } from './analytics.js';

/**
 * @param {string} base e.g. "https://chateaupapounan.fr/wp-json/papounan/v1/"
 */
/**
 * Page load (shared with the dev mock): POST /start, then the session opened with the challenge it carried and
 * the calendar requested in the same POST /session. Resolves to {status: 200, body: {config, rooms, days, minStay}}
 * or the error response (minStay = the legend's range of room minimum stays, from the server).
 * withCalendar = false (« merci » page, 0.10.0): the session only; resolves to {config, rooms}.
 *
 * @param {{start: () => Promise<{status, body}>, session: {start: Function}, calendar: (range: object) => Promise<{status, body}>}} io
 */
export async function loadPage(io, from, to, options = {}) {
	let internal = false;
	const start = async () => {
		const s = await io.start();
		internal = s.status === 200 && s.body?.internal === true;
		return s;
	};
	const r = await openPage({ ...io, start }, from, to, options);
	// Matomo (1.6.0): the visitor's category is known once /start answered (`internal`, per request); held events go out.
	context(internal);
	if (r.status !== 200) {
		trackLoadFailure(r);
	}
	return r;
}

async function openPage({ start, session, calendar }, from, to, { withCalendar = true } = {}) {
	const s = await start();
	if (s.status !== 200) {
		return s;
	}
	const { config, rooms, challenge, challengeError } = s.body;
	const first = challenge
		? { challenge: { status: 200, body: { challenge } }, extra: withCalendar ? { calendar: { from, to } } : {} }
		: { challenge: { status: challengeError?.status ?? 401, body: { error: { code: challengeError?.code ?? 'SESSION_REQUIRED', details: {} } } } };
	const opened = await session.start(first);
	if (opened.status !== 200) {
		return opened;
	}
	if (!withCalendar) {
		return { status: 200, body: { config, rooms } };
	}
	let cal = opened.body?.calendar;
	if (!cal?.days) {
		// Session opened earlier, or the calendar was refused with it: ask for it inside the session.
		const c = await calendar({ from, to });
		if (c.status !== 200) {
			return c;
		}
		cal = c.body;
	}
	// Phase 3: the window's end (`to`) and the legends per month / per two months are the server's.
	return { status: 200, body: { config, rooms, days: cal.days, to: cal.to ?? null, minStay: cal.minStay ?? null, minStayByMonth: cal.minStayByMonth ?? {}, minStayByWindow: cal.minStayByWindow ?? {} } };
}

/**
 * @param {string} base API base URL.
 * @param {{solver?: Function, early?: Promise<{status, body}>|null}} [opts] early = POST /start already sent by
 *   the inline script in the head (full pages): used once, by load().
 */
export function createApi(base, { solver, early = null } = {}) {
	const root = base.endsWith('/') ? base : base + '/';

	async function call(method, path, params = {}, headers = {}) {
		let url = root + path;
		const init = { method, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...headers } };
		if (method === 'GET') {
			const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null));
			url += (url.includes('?') ? '&' : '?') + qs.toString();
		} else {
			init.headers['Content-Type'] = 'application/json';
			init.body = JSON.stringify(params);
		}
		try {
			const response = await fetch(url, init);
			let body;
			try {
				body = await response.json();
			} catch (_e) {
				body = {}; // Not our JSON (e.g. an edge error page): the caller decides from the status.
			}
			const answer = { status: response.status, body };
			trackApi(path, answer);
			return answer;
		} catch (_e) {
			const failed = { status: 0, body: { error: { code: 'GENERIC', details: {} } } };
			trackApi(path, failed);
			return failed;
		}
	}

	const session = createSession({
		challenge: () => call('POST', 'session/challenge'),
		open: (payload, extra = {}) => call('POST', 'session', { payload, ...extra }),
		...(solver ? { solver } : {})
	});
	const gated = (path, body, headers = {}) => session.guard(() => call('POST', path, body, headers));
	function takeEarly() {
		const answer = early;
		early = null;
		return answer;
	}

	/** Sends POST /start now (compact search: at the click, while the panel chunk loads); load() uses it. */
	function prefetchStart() {
		early ??= call('POST', 'start');
	}

	return {
		prefetchStart,
		load: (from, to) => loadPage({ start: () => (early ? takeEarly() : call('POST', 'start')), session, calendar: (range) => gated('calendar', range) }, from, to),
		config: () => call('GET', 'config'),
		rooms: () => call('GET', 'rooms'),
		session: () => session.start(),
		calendar: (from, to) => gated('calendar', { from, to }),
		departures: (arrival, adults, children) => gated('calendar/departures', { arrival, adults, children }),
		stayCheck: (arrival, departure, adults, children) => gated('stay-check', { arrival, departure, adults, children }),
		search: (body) => gated('search', body),
		quote: (body) => gated('quote', body),
		// Checkout (0.10.0, 02 §4.8–§4.11): the « merci » page opens the session without the calendar.
		open: () => loadPage({ start: () => (early ? takeEarly() : call('POST', 'start')), session, calendar: (range) => gated('calendar', range) }, null, null, { withCalendar: false }),
		/** Booking proof of work (02 §11.4): the harder challenge, solved with the session's solver; null on failure. */
		bookingProof: async () => {
			const c = await call('POST', 'session/challenge', { purpose: 'booking' });
			return c.status === 200 && c.body?.challenge ? (solver || solve)(c.body.challenge) : null;
		},
		/** POST /bookings with its Idempotency-Key (one per attempt; a retry after a network failure reuses it). */
		bookings: (body, key) => gated('bookings', body, { 'Idempotency-Key': key }),
		bookingStatus: (ref, token) => gated('bookings/status', { ref, token }),
		bookingPayment: (body) => gated('bookings/payment', body),
		/**
		 * Matomo (1.6.0) for the store and the checkout: through the client, not imported by those lazy chunks (an import
		 * there puts analytics.js in a chunk of its own: one more request on every page, measured).
		 */
		analytics: { track, forbid, mark }
	};
}
